// path: frontend/src/components/Modals/VacNowModal.jsx
/**
 * Список сотрудников, находящихся в отпусках сегодня.
 *
 * Связь «отпуск → замещения» считается на лету по SubstitutionDto[]:
 * из db.employees берём employee.substitutions, фильтруем те, у которых
 * fromId === vacation.empId и период покрывает отпуск целиком. Раньше
 * использовалась findVacationSubstitutes из utils/substitution.js —
 * удалено, источник правды — employee.substitutions с бэкенда.
 *
 * Модалка read-only: связь читается для колонки «Замещает», ни одна
 * сущность не мутируется.
 */
import { useMemo, useState } from "react";
import { ModalShell } from "../ModalShell";
import { Select } from "../Select";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { optionsFromList } from "../../utils/selectOptions";
import { VACATION_TYPES } from "../../utils/constants";
import { TODAY, fmtDMY } from "../../utils/date";

function substitutesForVacation(vacation, employees) {
  const result = [];
  for (const emp of employees) {
    const subs = emp.substitutions ?? [];
    for (const s of subs) {
      if (s.fromId !== vacation.empId) continue;
      if (s.start > vacation.start) continue;
      if (s.end !== null && s.end < vacation.end) continue;
      result.push({ id: s.id, toId: emp.id });
    }
  }
  return result;
}

export function VacNowModal({ db, onClose, toast }) {
  const [filterDept, setFilterDept] = useState("all");
  const [sort, setSort] = useState("start");
  const { primaryDept, empName } = useDataHelpers(db);

  const deptSelectOptions = useMemo(
    () =>
      optionsFromList(db.departments, "Все подразделения", (d) => ({
        value: d.id,
        label: d.name,
      })),
    [db.departments],
  );

  const rows = useMemo(() => {
    return db.vacations
      .filter(
        (v) => v.status === "approved" && v.start <= TODAY && TODAY <= v.end,
      )
      .map((v) => {
        const e = db.employees.find((x) => x.id === v.empId);
        return {
          v,
          e,
          dept: primaryDept(e),
          substitutes: substitutesForVacation(v, db.employees),
        };
      })
      .filter(
        (r) => filterDept === "all" || (r.dept && r.dept.id === filterDept),
      )
      .sort((a, b) =>
        sort === "start"
          ? a.v.start < b.v.start
            ? -1
            : 1
          : a.v.end < b.v.end
            ? -1
            : 1,
      );
  }, [db.vacations, db.employees, filterDept, sort, primaryDept]);

  return (
    <ModalShell
      title="Сотрудники в отпусках"
      onClose={onClose}
      width={880}
      showSave={false}
      footer={
        <div className="modal-foot">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>
            Закрыть
          </button>
        </div>
      }
    >
      <div className="toolbar">
        <Select
          className="sm"
          value={filterDept}
          onChange={setFilterDept}
          options={deptSelectOptions}
        />
        <div className="seg sm">
          <button
            className={"seg-btn" + (sort === "start" ? " on" : "")}
            onClick={() => setSort("start")}
          >
            по началу
          </button>
          <button
            className={"seg-btn" + (sort === "end" ? " on" : "")}
            onClick={() => setSort("end")}
          >
            по окончанию
          </button>
        </div>
        <div className="spacer" />
        <button
          className="btn ghost sm"
          onClick={() => toast("Список обновлён")}
        >
          ⟳ Обновить
        </button>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Подразделение (основное)</th>
            <th>Начало</th>
            <th>Окончание</th>
            <th>Тип</th>
            <th>Комментарий</th>
            <th>Замещает</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.v.id}>
              <td>
                <b>{r.e ? `${r.e.last} ${r.e.first}` : "-"}</b>
              </td>
              <td>{r.dept?.name || "-"}</td>
              <td>{fmtDMY(r.v.start)}</td>
              <td>{fmtDMY(r.v.end)}</td>
              <td>{VACATION_TYPES[r.v.type]}</td>
              <td className="mut">{r.v.comment || "-"}</td>
              <td>
                {r.substitutes.length
                  ? r.substitutes.map((s) => empName(s.toId)).join(", ")
                  : "-"}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan="7" className="mut">
                Сейчас никто не находится в отпуске
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ModalShell>
  );
}

export default VacNowModal;
